import { createMongoAbility, subject, type MongoAbility, type Subject } from "@casl/ability";
import userStore from "@/store/user";
import User from "@/models/UserModel";
import {
  CONCESSOES_BASE,
  CONCESSOES_CUSTOMIZADAS,
  GRUPOS,
  PAPEIS,
  type NomePapelDoCatalogo,
} from "./catalogo";
import { resolverPapeis } from "./catalogo/legado";
import { compilarConcessao, type ConcessaoLegivel, type RegraCompilada } from "./compilador";
import { aplicarEscopo } from "./escopo";
import { conditionsMatcherSemCaixaEmEmail } from "./matcher-email";
import { montarAlcanceDeModulo, alcancaModulo, chave } from "./gates";
import type { Acao, Recurso } from "./catalogo/vocabulario";

/**
 * @deprecated Use o getter reativo `ehCliente` do store de usuário (T48).
 *
 * Variável de módulo escrita como efeito colateral de `defineAbilityFor`: quem a
 * importa recebe o valor do momento do import e nunca é notificado de mudança.
 * Continua exportada porque `FormProjetos.vue` e `DashboardGeral.vue` a importam, e
 * trocá-los custa 83 warnings manuais mais 24 erros de tipo que o `@ts-nocheck`
 * esconde — medido, e registrado como dívida com dono no `tasks.md` de T48.
 *
 * O que T48 já corrigiu aqui: o valor deriva de `tipo`, não de `roles`.
 */
export let isCliente = false;

/**
 * As quatro strings ambíguas Gali×cliente (`PADRONIZACAO-ROLES.md` §4.2):
 * `catalogo/legado.ts` as mapeia para `null` de propósito — `resolverPapeis`
 * não recebe `tipo`, e resolver por nome sozinho misturaria os dois lados. É
 * aqui, no agregador (T129, prerequisito 3), que a ambiguidade se resolve: o
 * mesmo `roles` grava a MESMA string para os dois tipos, e só o `tipo` do
 * usuário diz qual das duas listas usar.
 *
 * **Lado Gali**, medido contra os arquivos imperativos antes de apagá-los:
 * - `projetista` → `projetos.ts:11` concede `projetos.leitura`; `projetos_fabrica.ts:12`
 *   concede `projetos_fabrica.leitura` para a MESMA string — cruza domínio.
 * - `projetista_fabrica` → só `projetos_fabrica.ts:12` (`projetos_fabrica.leitura`).
 * - `aprovador_obra` → nenhum arquivo Gali lê esta string; fica vazio.
 * - `aprovador_fabrica` → `projetos_fabrica.ts:22` concedia só `update`@`PROJETO`
 *   em `Situacao: ["PAP"]`. **Amplia, medido e declarado**: não existe papel 1:1
 *   para "só `update`", e `projetos.aprovacao` (o papel mais próximo, origem no
 *   ramo cliente) também dá `update_status` e `read` no mesmo estado. Sem papel
 *   novo só para isto — vai para a lista de avisos à área, mesmo padrão de
 *   `site_manager`/`pcp`/`mkt_read` (AD-034).
 */
const AMBIGUAS_GALI: Record<string, readonly NomePapelDoCatalogo[]> = {
  projetista: ["projetos.leitura", "projetos_fabrica.leitura"],
  projetista_fabrica: ["projetos_fabrica.leitura"],
  aprovador_obra: [],
  aprovador_fabrica: ["projetos.aprovacao"],
};

/**
 * **Lado cliente**, medido contra `cliente.ts` antes de apagá-lo:
 * - `projetista` → `GRUPOS.CLIENTE_LEITOR` (`projetos.leitura_externa` +
 *   `obra.leitura`) é exatamente o que `cliente.ts:29-32` (T63) dava.
 * - `projetista_fabrica` → `cliente.ts:36-40` dava `read` em PROJETO_FABRICA/
 *   FABRICACAO/Produto e `comentario` em PROJETO, sem `read` em PROJETO.
 *   **Amplia, medido e declarado**: reaproveitar `GRUPOS.CLIENTE_LEITOR` (que
 *   carrega `projetos.leitura_externa`) soma `read`@PROJETO, que esta string
 *   sozinha não tinha. Criar um papel só para excluir esse `read` seria um
 *   sexto papel para uma diferença de uma célula; aviso à área.
 * - `aprovador_obra` → `GRUPOS.CLIENTE_APROVADOR` (`projetos.leitura_externa` +
 *   `obra.leitura` + `projetos.aprovacao`) é `cliente.ts:50-54`, com a restrição
 *   de 4 para 1 estado já decidida (T56, AD-031 D4b).
 * - `aprovador_fabrica` → `cliente.ts:42-48` mais o padrão de leitura externa de
 *   fábrica. **Mesma amplificação de `projetista_fabrica`** (ganha `comentario`
 *   e `read`@PROJETO incondicional que a string sozinha não tinha).
 */
const AMBIGUAS_CLIENTE: Record<string, readonly NomePapelDoCatalogo[]> = {
  projetista: [...GRUPOS.CLIENTE_LEITOR],
  projetista_fabrica: [...GRUPOS.CLIENTE_LEITOR, "projetos_fabrica.leitura_externa"],
  aprovador_obra: [...GRUPOS.CLIENTE_APROVADOR],
  aprovador_fabrica: [...GRUPOS.CLIENTE_APROVADOR, "projetos_fabrica.leitura_externa"],
  // Nao e ambigua: so tem lado cliente (`AMBIGUAS_GALI` nao a lista, entao um Gali com
  // a string nao ganha nada — como em `cliente.ts`, onde a regra vivia dentro do ramo
  // cliente). Mora aqui porque e a unica tabela resolvida por `tipo`.
  materiais_cliente: ["materiais.leitura_externa"],
};

/** As 4 strings ambíguas que o usuário detém, traduzidas pela tabela do seu `tipo`. */
function papeisAmbiguos(user: User): NomePapelDoCatalogo[] {
  const tabela = user.tipo === "cliente" ? AMBIGUAS_CLIENTE : AMBIGUAS_GALI;
  const roles = user.roles ?? [];
  return Object.keys(tabela)
    .filter((bruto) => roles.includes(bruto))
    .flatMap((bruto) => [...tabela[bruto]]);
}

/** Só os nomes que o catálogo de fato declara — papel legado sem slice (nenhum hoje) some aqui, não quebra. */
function papeisDeclarados(nomes: readonly string[]): NomePapelDoCatalogo[] {
  return nomes.filter((nome): nome is NomePapelDoCatalogo =>
    Object.prototype.hasOwnProperty.call(PAPEIS, nome),
  );
}

/**
 * `acesso.total` (`manage all`, os 30 recursos) exige `user.email` — defesa
 * herdada do `index.ts` de antes da T129: os dois `can()` hard-coded que hoje
 * viraram este papel só rodavam dentro de `if (user?.email)`
 * (`detemPapel(...) -> can("manage","all")`), nunca dentro do `if (user)` que
 * compunha os 16/17 domínios comuns. A T129 unificou os dois caminhos num só
 * (`resolverPapeis` + `PAPEIS`), e por isso precisa deste filtro nomeado para
 * não perder a defesa: sem ele, um usuário com objeto válido mas e-mail vazio
 * — hoje só um artefato de fixture de teste, `usuarioComRoles([], { email: null })`
 * — herdaria `manage all` só por ter a role, o que o código anterior nunca
 * permitia. Nenhum outro papel tinha esse gate no código anterior — só os dois
 * que colapsaram em `acesso.total` (AD-026) — e por isso o filtro é por NOME,
 * não uma regra geral de "papel admin exige email".
 */
function semAcessoTotalSemEmail(nomes: readonly NomePapelDoCatalogo[], user: User) {
  if (user.email) return nomes;
  return nomes.filter((nome) => nome !== "acesso.total");
}

/**
 * Os nomes de papel canônicos que o usuário de fato detém, já resolvidos
 * (legado traduzido, ambíguas Gali×cliente desambiguadas por `tipo`,
 * `acesso.total` sem e-mail filtrado) — a MESMA lista que alimenta a Ability.
 *
 * Exportada para as telas de governança (T109 em diante, GOV-03 a GOV-06):
 * `dominiosQueAdministra`/`validarGrupoCustomizado`/`diffDeAtribuicao`
 * (`administracao.ts`, T108) pedem `Usuario.papeis` já resolvido — não
 * `user.roles` cru — e esta é a fronteira que traduz um sem reimplementar a
 * tradução dentro da tela (o painel consome o kernel, não reescreve política).
 */
export function papeisResolvidosDoUsuario(user: User): readonly NomePapelDoCatalogo[] {
  const { traduzidos } = resolverPapeis(user.roles ?? []);
  return semAcessoTotalSemEmail(papeisDeclarados([...traduzidos, ...papeisAmbiguos(user)]), user);
}

/**
 * Mesma tradução legado→canônico de `papeisResolvidosDoUsuario`, para um ALVO
 * que não é o usuário logado — a pessoa que vai RECEBER papel (T111,
 * `AtribuirAcesso.vue`, GOV-05). `PessoasApi.getAllPessoas` devolve só
 * `roles: string[]` cru, nunca uma instância de `User` — não há `tipo` nem
 * `email` para resolver a ambiguidade Gali×cliente ou o filtro de
 * `acesso.total`, e não precisa: aqui não se monta Ability, só se mede o que
 * a pessoa JÁ TEM para o diff de `diffDeAtribuicao` (T108), e o alvo desta
 * tela é sempre pessoal Gali (o combobox vem de `PessoasApi`, não de conta
 * cliente).
 */
export function papeisResolvidosDeRoles(roles: readonly string[]): readonly NomePapelDoCatalogo[] {
  const { traduzidos } = resolverPapeis([...roles]);
  return papeisDeclarados(traduzidos);
}

/**
 * Tudo que o agregador precisa do usuário para montar a Ability e o gate de
 * módulo — resolvido uma vez, consumido pelos dois (T129, CAT-06).
 */
function catalogoDoUsuario(user: User) {
  // `traduzidos` PRECISA ser o bruto de `resolverPapeis` — sem o filtro de
  // `papeisDeclarados`/`semAcessoTotalSemEmail` que `papeisResolvidosDoUsuario`
  // aplica, e sem `papeisAmbiguos` — porque é exatamente o que
  // `concessoesCustomizadasDeAcesso` (`papeis/acesso.ts`) sempre recebeu.
  // Reusar `papeisResolvidosDoUsuario` aqui mudaria o insumo desse `.some()`
  // e seria uma mudança de comportamento não pedida por T109 (R11).
  const { traduzidos } = resolverPapeis(user.roles ?? []);
  const nomes = papeisResolvidosDoUsuario(user);
  const papeis = nomes.map((nome) => PAPEIS[nome]);
  const concessoes: readonly ConcessaoLegivel[] = [
    ...CONCESSOES_BASE,
    ...papeis.flatMap((papel) => [...papel.concessoes]),
  ];
  const customizadas: readonly RegraCompilada[] = [
    ...CONCESSOES_CUSTOMIZADAS.biblioteca(user.todasPermissoes),
    ...CONCESSOES_CUSTOMIZADAS.acesso(traduzidos),
  ];

  return { concessoes, customizadas };
}

export default function defineAbilityFor(user: User | null): MongoAbility {
  // So o objeto precisa existir — igual ao `if (user)` de antes da T129, que
  // compunha os 16/17 dominios comuns independente de e-mail. So `acesso.total`
  // exige e-mail, e esse filtro nomeado mora em `semAcessoTotalSemEmail`.
  if (!user) return createMongoAbility([]);

  isCliente = user.tipo === "cliente";

  const { concessoes, customizadas } = catalogoDoUsuario(user);
  const regras = concessoes.flatMap((concessao) =>
    // T158: `delegacoes` passa a viajar daqui para `compilarConcessao` —
    // antes só `{ email }` chegava, e `identidadesDe`/`menorFimDeDelegacaoAtiva`
    // (T113/T114, DEL-01/DEL-02) nunca recebiam delegação nenhuma, mesmo
    // corretas e testadas em isolamento (`compilador.spec.ts`,
    // `delegacao.spec.ts`). Inerte em produção hoje: `user.delegacoes` só
    // deixa de ser `undefined` quando o backend entregar o campo (BE-11).
    compilarConcessao(concessao, { email: user.email, delegacoes: user.delegacoes }),
  );

  // `aplicarEscopo` só age sobre `tipo: "cliente"` (T59/T61/T63); para Gali as
  // regras saem intactas. `propostas: []` é fail-closed enquanto o backend não
  // devolver id estável de proposta (issue BE-04, `docs/contratos-backend.md`).
  const escopadas = aplicarEscopo([...regras, ...customizadas], {
    tipo: user.tipo,
    escopo: { empreendimentos: user.obra ?? [], propostas: [] },
  });

  return createMongoAbility(escopadas as never, {
    conditionsMatcher: conditionsMatcherSemCaixaEmEmail,
  });
}

/**
 * @deprecated Autoriza por par ação/sujeito, aceitando o sujeito como
 * `string` — a mesma abertura que deixa regra condicional (`exigeRelacao`,
 * escopo, estado) ser avaliada pelo *tipo* do recurso, não pelo registro, e
 * devolver `true` onde deveria negar (ADR-0003, P1-3/P1-4 do spec). Use
 * `pode(acao, registro)` para checagem por registro (ABAC) ou
 * `podeNoModulo(acao, recurso)` para o gate de módulo. Mantido enquanto
 * `.permissoes-importadores-baseline.json` tiver importadores — T11 não
 * migra nenhum call site.
 */
export function can(action: string, subject: Subject, field?: string) {
  const user = userStore().user;
  const ability = defineAbilityFor(user);
  return ability.can(action, subject, field);
}

export function cannot(action: string, subject: Subject, field?: string) {
  return !can(action, subject, field);
}

/**
 * Autoriza por REGISTRO, nunca por nome de recurso em string.
 *
 * `T extends object` é a restrição que torna `pode("update", "RDO")` erro de
 * compilação, não decisão errada em runtime: uma string literal não satisfaz
 * `object`, então o TypeScript recusa a chamada antes dela chegar ao CASL.
 * É a distinção que fecha o defeito medido no spec (P1-3/P1-4): regra
 * condicional avaliada por tipo do recurso, em vez de pelo registro,
 * devolvendo `true` e anulando o ABAC.
 */
export function pode<T extends object>(acao: string, registro: T): boolean {
  const user = userStore().user;
  const ability = defineAbilityFor(user);
  return ability.can(acao, registro as Subject);
}

/**
 * Gate de MÓDULO: aqui a string de recurso é legítima, porque a pergunta é
 * "esse papel alcança esse recurso" — não "esse registro específico pode".
 * Nenhuma condição por registro (`exigeRelacao`, escopo, estado) é avaliada
 * aqui; isso é papel de `pode`, sobre o registro real.
 *
 * Precomputa o alcance a partir do `Set` de `gates.ts` (T57, T129) — não mais
 * `ability.can(acao, recurso)` sobre sujeito string, que o AD-046 mediu dar
 * falso positivo em regra condicional (P1-5 do spec).
 */
export function podeNoModulo(acao: string, recurso: string): boolean {
  const user = userStore().user;
  if (!user) return false;

  const { concessoes, customizadas } = catalogoDoUsuario(user);
  const alcance = new Set(montarAlcanceDeModulo(concessoes));

  // As regras customizadas (hatch, T58) entram no gate só quando incondicionais:
  // condição é justamente o que este gate não avalia (mesma regra de
  // `ehCondicional` em `gates.ts`). Hoje só o hatch de `acesso` produz regra
  // sem condição (`editar@Permissao`); o de `biblioteca` é ACL por registro e
  // nunca entra aqui — mesmo critério, aplicado à forma `RegraCompilada`.
  for (const regra of customizadas) {
    if (regra.conditions && Object.keys(regra.conditions).length > 0) continue;
    for (const acaoDaRegra of regra.action) alcance.add(chave(acaoDaRegra, regra.subject));
  }

  return alcancaModulo(alcance, acao as Acao, recurso as Recurso);
}

import { sujeitoDeRdoComObra, prototipoDeRdoParaCreate } from "./catalogo/relacoes";

export { sujeitoDeRdoComObra, prototipoDeRdoParaCreate };

/**
 * Criar diário vale só na obra em que a pessoa é site manager (ou suplente) —
 * RDO-01. Em `create` o diário ainda não existe: a relação sai da obra, pelo
 * protótipo. Sem obra carregada nega (falha fechada).
 */
export function podeCriarRdoNaObra(obra: unknown): boolean {
  if (!obra) return false;
  return pode("create", subject("RDO", prototipoDeRdoParaCreate(obra)));
}
